import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useShopDomain } from './useShopDomain'

/**
 * Composable for getting the current shop context in storefront views.
 *
 * This composable provides a unified way to get the shopCode regardless of whether
 * the user is accessing the shop via:
 * - Main domain with URL path: staging.zaveato.co.uk/shop/myshop
 * - Subdomain: myshop.staging.zaveato.co.uk/
 * - Custom domain: mybusiness.com/
 *
 * It also provides helper functions for generating URLs that work correctly
 * in both contexts.
 */
export function useStorefrontShop() {
  const route = useRoute()
  const { isCustomDomain, resolvedShop } = useShopDomain()

  /**
   * Get the current shop code from either:
   * 1. URL params (when on main domain: /shop/:shopCode)
   * 2. Resolved domain (when on subdomain/custom domain)
   */
  const shopCode = computed(() => {
    // First check URL params (main domain access)
    if (route.params.shopCode) {
      return route.params.shopCode as string
    }

    // Then check resolved domain (subdomain/custom domain access)
    if (resolvedShop.value?.shopCode) {
      return resolvedShop.value.shopCode
    }

    return null
  })

  /**
   * Whether we're on a custom domain (subdomain or fully custom)
   */
  const isOnCustomDomain = computed(() => isCustomDomain.value)

  /**
   * Get the shop ID from resolved shop (only available on custom domains)
   */
  const shopId = computed(() => resolvedShop.value?.shopId || null)

  /**
   * Get the shop name from resolved shop (only available on custom domains)
   */
  const shopName = computed(() => resolvedShop.value?.shopName || null)

  /**
   * Generate a URL path for the storefront.
   * On custom domains, returns root-relative paths (e.g., /products)
   * On main domain, returns shop-prefixed paths (e.g., /shop/myshop/products)
   */
  function getStorefrontPath(path: string = ''): string {
    // Normalize path to start without slash
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path

    if (isCustomDomain.value) {
      // On custom domain, use root paths
      return normalizedPath ? `/${normalizedPath}` : '/'
    } else {
      // On main domain, prefix with /shop/:shopCode
      const code = shopCode.value
      if (!code) {
        console.warn('No shopCode available for generating storefront path')
        return '/'
      }
      return normalizedPath ? `/shop/${code}/${normalizedPath}` : `/shop/${code}`
    }
  }

  /**
   * Generate route object for router-link or programmatic navigation.
   *
   * `shopCodeOverride` MUST be passed by callers whose page has no
   * `:shopCode` URL param (cart, cart dropdown, homepage suggestion strips)
   * but whose target belongs to a KNOWN shop — e.g. a cart line or a
   * "you might also like" recommendation. Without it, the composable's
   * `shopCode` falls back to `null` (no `:shopCode` in the current URL to
   * read from) and vue-router throws `Missing required param "shopCode"`.
   * On a page that DOES have `:shopCode` in the URL, the override can be
   * omitted — the composable resolves it from the route.
   */
  function getStorefrontRoute(
    name: string,
    params: Record<string, string> = {},
    shopCodeOverride?: string | null,
  ) {
    if (isCustomDomain.value) {
      // On custom domain, use the Storefront* route names (without shopCode param)
      return {
        name: `Storefront${name}`,
        params
      }
    } else {
      // On main domain, use the Shop* route names (with shopCode param).
      // Prefer the explicit override, then fall back to the route param.
      const code = shopCodeOverride ?? shopCode.value
      return {
        name: `Shop${name}`,
        params: { shopCode: code ?? '', ...params }
      }
    }
  }

  /**
   * Get home route for the current shop
   */
  const homeRoute = computed(() => getStorefrontRoute('Home'))

  /**
   * Get products route for the current shop
   */
  const productsRoute = computed(() => getStorefrontRoute('Products'))

  /**
   * Get cart route for the current shop
   */
  const cartRoute = computed(() => getStorefrontRoute('Cart'))

  /**
   * Get checkout route for the current shop
   */
  const checkoutRoute = computed(() => getStorefrontRoute('Checkout'))

  /**
   * Get product detail route.
   *
   * `shopCodeOverride` — see `getStorefrontRoute`. Any caller whose page
   * has no `:shopCode` URL param (cart / cart dropdown / homepage strips)
   * MUST pass the product's shopCode explicitly, otherwise vue-router
   * throws "Missing required param 'shopCode'".
   */
  function getProductRoute(slug: string, shopCodeOverride?: string | null) {
    return getStorefrontRoute('ProductDetail', { slug }, shopCodeOverride)
  }

  /**
   * Get order confirmation route
   */
  const orderConfirmationRoute = computed(() => getStorefrontRoute('OrderConfirmation'))

  /**
   * Get customer order view route
   */
  function getOrderRoute(orderNumber: string) {
    return getStorefrontRoute('OrderView', { orderNumber })
  }

  return {
    shopCode,
    shopId,
    shopName,
    isOnCustomDomain,
    getStorefrontPath,
    getStorefrontRoute,
    homeRoute,
    productsRoute,
    cartRoute,
    checkoutRoute,
    orderConfirmationRoute,
    getProductRoute,
    getOrderRoute
  }
}

export default useStorefrontShop
